import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

const CACHE_TTL_MS = 5 * 60 * 1000;
// Versioned cache prefix: bump this constant whenever translation namespaces
// change shape. Old LocalStorage entries (`i18n_cache_*`) are ignored once the
// prefix changes, so a stale translation bundle can never re-surface raw keys
// after a hot-reload / deploy.
const CACHE_PREFIX = 'i18n_cache_v2_';

function getCacheKey(lng, ns) {
  return `${CACHE_PREFIX}${lng}_${ns}`;
}

function loadFromCache(lng, ns) {
  try {
    const raw = localStorage.getItem(getCacheKey(lng, ns));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(getCacheKey(lng, ns));
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function saveToCache(lng, ns, data) {
  try {
    localStorage.setItem(getCacheKey(lng, ns), JSON.stringify({
      data,
      timestamp: Date.now(),
    }));
  } catch {
    // localStorage full or unavailable - silently ignore
  }
}

const backendOptions = {
  loadPath: '/api/v1/localization/{{lng}}/{{ns}}',
  crossDomain: true,
  withCredentials: true,
  request: (options, url, payload, callback) => {
    try {
      const match = url.match(/\/localization\/([^/]+)\/([a-zA-Z0-9-]+)/);
      let lng = match ? match[1] : null;
      const ns = match ? match[2] : null;

      // Normalize region-suffixed languages (en-US -> en, ar-EG -> ar).
      // The backend only supports 'en' and 'ar' — a raw en-US URL returns 400.
      if (lng && lng.includes('-')) {
        lng = lng.split('-')[0];
        url = url.replace(match[1], lng);
      }

      if (lng && ns) {
        const cached = loadFromCache(lng, ns);
        if (cached) {
          callback(null, { status: 200, data: JSON.stringify(cached) });
          return;
        }
      }

      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.withCredentials = true;
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            if (lng && ns) {
              try {
                const data = JSON.parse(xhr.responseText);
                saveToCache(lng, ns, data);
              } catch {}
            }
            callback(null, { status: xhr.status, data: xhr.responseText });
          } else {
            callback(xhr.statusText, { status: xhr.status, data: xhr.responseText });
          }
        }
      };
      xhr.send();
    } catch (err) {
      callback(err, { status: 500 });
    }
  },
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'ar'],
    debug: process.env.NODE_ENV === 'development',

    // Load only the common namespace globally. All other namespaces (login, admin, seller, etc.)
    // are lazy-loaded on-demand via useTranslation('namespace') or useTranslationNamespace().
    // This eliminates 7 redundant HTTP requests on every page load.
    // Backend request function includes localStorage caching with 5-minute TTL.
    ns: ['common'],
    defaultNS: 'common',

    // Enable lazy loading for other namespaces
    load: 'languageOnly',

    interpolation: {
      escapeValue: false,
      formatSeparator: ',',
    },

    backend: backendOptions,

    react: {
      useSuspense: false,
      transEmptyNodeValue: '',
    },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export default i18n;